/**
 * Theme. Two shapes are tolerated (local-first: offline docs hold the old shape
 * forever — see docs/plans/2026-06-26-design-system-theme-token-designer.md):
 *   - LEGACY `Theme` — curated preset + 4 knobs (this file). Existing sites keep
 *     this and render byte-identically; never auto-migrated.
 *   - NEW `SiteTheme` — Brand inputs + generationVersion (+ sparse overrides),
 *     from @website-builder/theme-engine. New sites + the Brand designer write this.
 * `SiteThemeOrLegacy` is the union stored on `Site.theme`.
 */
import * as Schema from "effect/Schema";
import { SiteTheme } from "@website-builder/theme-engine";

export const ThemePreset = Schema.Literal(
  "minimal",
  "bold",
  "editorial",
  "creative",
  "corporate",
);
export type ThemePreset = Schema.Schema.Type<typeof ThemePreset>;

/**
 * KnobValues — overrides on top of a preset's tokens. CSS color values
 * (any valid CSS string); font names from a curated list (enforced at
 * builder-api boundary, not at schema level).
 */
export const KnobValues = Schema.Struct({
  primaryColor: Schema.optional(Schema.String),
  accentColor: Schema.optional(Schema.String),
  bodyFont: Schema.optional(Schema.String),
  headingFont: Schema.optional(Schema.String),
});
export type KnobValues = Schema.Schema.Type<typeof KnobValues>;

export const Theme = Schema.Struct({
  preset: ThemePreset,
  knobs: KnobValues,
});
export type Theme = Schema.Schema.Type<typeof Theme>;

export { SiteTheme };

/**
 * What `Site.theme` holds: the new Brand-based `SiteTheme` or the legacy
 * preset/knobs `Theme`. Disjoint by required fields (`brand` vs `preset`), so the
 * union decodes unambiguously and old docs stay valid.
 */
export const SiteThemeOrLegacy = Schema.Union(SiteTheme, Theme);
export type SiteThemeOrLegacy = Schema.Schema.Type<typeof SiteThemeOrLegacy>;

/** Narrow the stored theme to the new Brand shape. */
export function isSiteTheme(t: SiteThemeOrLegacy): t is SiteTheme {
  return "brand" in t;
}
